import { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { Option, Select } from 'baseui/select';
import { ApiError, PaginationMeta } from 'common/repositories/common.model';

import { FontCaseType } from '../fields/text-field';

export enum ColumnDataType {
  text,
  number,
  select,
  selectMulti,
  email,
  delete,
  tags,
  date,
  time,
  checkbox,
  null,
}

export interface TableSelectInputCoreProps
  extends React.ComponentProps<typeof Select> {
  dataType: ColumnDataType;
  selectGetLastItem?: boolean;
  value?: any;
  options?: any[];
  isMulti?: boolean;
  isMenuOpen?: boolean;
  setIsMenuOpen?: (value: React.SetStateAction<boolean>) => void;
}

export interface TableSelectInputProps<
  ListQueryData,
  ListQueryVariables,
  DetailQueryData,
  DetailQueryVariables,
> extends Omit<TableSelectInputCoreProps, 'onChange'> {
  useListQueryHook?: (
    baseInputs: ListQueryVariables,
    baseOptions?: UseQueryOptions<ListQueryData, ApiError>,
  ) => UseQueryResult<ListQueryData, ApiError>;
  useDetailLazyQueryHook?: (
    baseInputs: DetailQueryVariables,
    baseOptions?: UseQueryOptions<DetailQueryData, ApiError>,
  ) => UseQueryResult<DetailQueryData, ApiError>;
  listTransformer?: (result: ListQueryData) => Option[];
  paginationTransformer?: (result: ListQueryData) => PaginationMeta;
  getMemoizedListVariables?: (
    page: number,
    query?: string,
  ) => ListQueryVariables;
  ref?: any;
  onChange?: (val: any, extra?: any) => void;
  detailTransformer?: (result: DetailQueryData) => Option;
  getMemoizedDetailVariables?: (value: string) => DetailQueryVariables;
  listKeyFunction?: Function;
  detailKeyFunction?: Function;
  staleTime?: number;
  enabled?: boolean;
}

export interface AsyncTableSelectInputProps<
  ListQueryData,
  ListQueryVariables,
  DetailQueryData,
  DetailQueryVariables,
> extends TableSelectInputProps<
    ListQueryData,
    ListQueryVariables,
    DetailQueryData,
    DetailQueryVariables
  > {
  useListQueryHook: (
    baseInputs: ListQueryVariables,
    baseOptions?: UseQueryOptions<ListQueryData, ApiError>,
  ) => UseQueryResult<ListQueryData, ApiError>;
  useDetailLazyQueryHook: (
    baseInputs: DetailQueryVariables,
    baseOptions?: UseQueryOptions<DetailQueryData, ApiError>,
  ) => UseQueryResult<DetailQueryData, ApiError>;
  listTransformer: (result: ListQueryData) => Option[];
  detailTransformer: (result: DetailQueryData) => Option;
  getMemoizedDetailVariables: (value: string) => DetailQueryVariables;
  paginationTransformer: (result: ListQueryData) => PaginationMeta;
  getMemoizedListVariables: (
    page: number,
    query?: string,
  ) => ListQueryVariables;
  renderCreate?: boolean;
  onClickCreate?: () => void;
}

export interface AsyncTableWithoutDetailSelectInputProps<
  ListQueryData,
  ListQueryVariables,
  DetailQueryData,
  DetailQueryVariables,
> extends TableSelectInputProps<
    ListQueryData,
    ListQueryVariables,
    DetailQueryData,
    DetailQueryVariables
  > {
  useListQueryHook: (
    baseInputs: ListQueryVariables,
    baseOptions?: UseQueryOptions<ListQueryData, ApiError>,
  ) => UseQueryResult<ListQueryData, ApiError>;
  listTransformer: (result: ListQueryData) => Option[];
  paginationTransformer: (result: ListQueryData) => PaginationMeta;
  getMemoizedListVariables: (
    page: number,
    query?: string,
  ) => ListQueryVariables;
  renderCreate?: boolean;
  onClickCreate?: () => void;
}

export interface AsyncTableMultiSelectInputProps<
  ListQueryData,
  ListQueryVariables,
  DetailQueryData,
  DetailQueryVariables,
> extends TableSelectInputProps<
    ListQueryData,
    ListQueryVariables,
    DetailQueryData,
    DetailQueryVariables
  > {
  useListQueryHook: (
    baseInputs: ListQueryVariables,
    baseOptions?: UseQueryOptions<ListQueryData, ApiError>,
  ) => UseQueryResult<ListQueryData, ApiError>;
  listTransformer: (result: ListQueryData) => Option[];
  getMemoizedListVariables: (
    page: number,
    query?: string,
  ) => ListQueryVariables;
  paginationTransformer: (result: ListQueryData) => PaginationMeta;
  renderCreate?: boolean;
  onClickCreate?: () => void;
}

export type IColumn<T> = ColumnDef<T> & {
  dataType: ColumnDataType;
  options?: any[];
  selectGetLastItem?: boolean;
  disabled?: boolean;
  maxTags?: number;
  checkboxNoLabel?: boolean;
  stickyLeft?: boolean;
  stickyRight?: boolean;
  tagKeyName?: string;
  selectProps?: TableSelectInputProps<any, any, any, any>;
  required?: boolean;
  fontCase?: FontCaseType;

  minNumberKey?: string;
  maxNumberKey?: string;
  minNumber?: number;
  maxNumber?: number;
};

export interface TableEditableComponentProps<T> {
  data: T[];
  columns: IColumn<any>[];
  onRowClick?: (value: { original: any; index: number }) => void;
  selectedRow?: number;
  onRenderAdditionalComponent?: React.ReactNode;
  dispatch: React.Dispatch<any>;
  disabled?: boolean;
  noAddRemoveRow?: boolean;
  hasPagination?: boolean;
  defaultPageSize?: number;
  headerColor?: string;
  name?: string;
  errors?: unknown;
  uniqueRowKey?: string;
  onCustomDisabledCell?: (params: {
    accessorKey: string;
    rowOriginal: T;
  }) => boolean;
  onCustomBackgroundColorCell?: (params: {
    accessorKey: string;
    rowOriginal: T;
  }) => string;
  tableContainerStyle?: React.CSSProperties;
}

export type ActionType = 'addRow' | 'update_cell' | 'removeRow';

export type CheckingByStateType<T> = {
  type: 'state';
  data: T[];
  isCheck: (data: T) => boolean;
};

export type CheckingByLengthType<T> = {
  type: 'lenght';
  actualData: T[];
  currentData: T[];
};
